import { defineComponent as _defineComponent } from 'vue'
import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import router from "@/router";
  import {
    computed,
    onMounted,
    ref,
    watch,
  } from "vue";
  import { useStore } from "vuex";
  import { EstateMapInterface } from "@/types/EstateMapInterface";
  import { UserRoles } from "@/enums/UserRoles";
  import { StageMapInterface } from "@/types/StageMapInterface";
  import { getStageStatusClass } from "@/helpers/status";
  import { StageStatus } from "@/enums/StageStatus";
  import { StageInterface } from "@/types/StageInterface";
  import { useIsDesktop } from "@/features/useIsDesktop";
  import StageNavMobile from "@/components/StageNavMobile.vue";
  import MapSidePanel from "@/components/MapSidePanel.vue";
  import Hammer from "hammerjs";
  import type HammerInput from "hammerjs";
  import pinchZoomHammer from "@/helpers/estateMap/mapsZoom";
  import { handleDragging, startDragging, stopDragging } from "@/helpers/zoom";
  import MapSideLegend from "@/components/MapSideLegend.vue";
  import EstateSvgMap from "@/components/maps/EstateSvgMap.vue";

  const mobileLayoutWidth = 680;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'EstateMap',
  props: {
    role: { type: String, default: () => UserRoles.LANDOWNER },
    viewType: { type: String, default: "" },
    isEmbedded: { type: Boolean, default: false },
    mapPage: { type: String, default: "" },
    zoom: { type: Number, default: 1 },
    lotsByHouseDesigns: { type: Array, required: false, default: undefined },
    builderView: { type: Boolean, required: false },
    plansView: { type: Boolean, required: false, default: false },
    estateId: { type: String, required: false, default: undefined },
    hideMapSidePanelOptions: { type: Boolean, required: false },
    businessId: { type: String, required: false, default: undefined },
  },
  emits: [
    "changeView",
    "updateZoom",
    "onRegister",
    "onMessage",
    "changeViewMap",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const emit = __emit;
  const store = useStore();
  const { isDesktop } = useIsDesktop(mobileLayoutWidth);
  const userId = router.currentRoute.value.params.userId;
  const estateId = computed(() => getEstateId());
  const map = ref<EstateMapInterface | null | "">(null);
  const stages = ref<StageMapInterface[]>([]);
  const props = __props;
  const stageSidePanelHover = ref({
    value: false,
    id: "",
  });
  const showConfirmDeleteModal = ref(false);
  const getAvailableStages = computed(() => {
    return stages.value.filter(
      (value) => value.stageId.status === StageStatus.NOW_SELLING
    );
  });

  const isEmbeddedDesktop = computed(() => isDesktop.value);

  const getEstateId = () => {
    if (props.role === UserRoles.LANDOWNER) {
      return store.state.user.selectedEstateId;
    } else if (props.estateId) {
      return props.estateId;
    } else {
      return router.currentRoute.value.params.estateId;
    }
  };

  const onSidePanelStageHover = (value: boolean, id: string) => {
    stageSidePanelHover.value = { value, id };
  };

  const onSidePanelStageClick = (id: string) => {
    const stage = stages.value.filter((s) => s.stageId._id === id);

    onStage(stage[0]);
  };

  const isCustomerRole = computed(() => {
    return props.role === UserRoles.CUSTOMER;
  });

  onMounted(() => {
    getMap();
    getStages();
  });

  const hoverStageId = ref("");

  const onStageHover = (id: string) => {
    hoverStageId.value = id;
  };

  const onStageLeave = () => {
    hoverStageId.value = "";
  };

  const isDragging = ref(false);
  const svgStartPosition = ref({ x: 0, y: 0 });
  const svgStartScrollPosition = ref({ x: 0, y: 0 });
  const svgContainerMap = ref();

  const zoomIn = () => {
    emit("updateZoom", 0.2);
  };

  const zoomOut = () => {
    emit("updateZoom", -0.2);
  };

  const startDrag = (event: MouseEvent) => {
    startDragging(
      event,
      props.zoom,
      isDragging,
      svgContainerMap,
      svgStartPosition,
      svgStartScrollPosition
    );
  };
  const handleDrag = (event: MouseEvent) => {
    handleDragging(
      event,
      isDragging,
      svgContainerMap,
      svgStartPosition,
      svgStartScrollPosition
    );
  };
  const stopDrag = () => {
    stopDragging(props.zoom, isDragging, svgContainerMap);
  };

  function getMap() {
    store
      .dispatch("estate/getEstateMap", estateId.value)
      .then((res) => {
        map.value = res.data;
      })
      .catch((error) => {
        if (error.response?.data?.statusCode === 404) {
          map.value = "";
        }
      });
  }
  const getStages = () => {
    store
      .dispatch("stage/getStagesMap", {
        estateId: estateId.value,
        isCustomer: isCustomerRole.value,
      })
      .then((res) => {
        stages.value = res.data;
      });
  };

  function toCreateMap() {
    router.push({
      name: "AdminEstateMapCreate",
      params: { userId, estateId: estateId.value },
    });
  }
  function toEditMap() {
    router.push({
      name: "AdminEstateMapUpdate",
      params: { userId, estateId: estateId.value },
    });
  }
  function deleteMap() {
    store.dispatch("estate/deleteEstateMap", map.value._id).then(() => {
      getMap();
    });
  }
  function onStage(stage: StageMapInterface) {
    const embed = props.isEmbedded.toString();

    if (props.builderView) {
      emit("changeViewMap");
      return;
    }

    if (props.isEmbedded && !isDesktop.value) {
      const { href } = router.resolve({
        name: "EstateMapV2",
        params: {
          estateId: estateId.value,
        },
      });

      window.open(`${href}?stageId=${stage.stageId._id}&embed=false`, "_blank");
      return;
    }

    if (props.role === UserRoles.LANDOWNER) {
      router.push({
        name: "Lots",
        params: { stageId: stage.stageId._id, embed },
        query: { map: "true" },
      });
      return;
    }

    if (props.role === UserRoles.SUPER_ADMIN) {
      router.push({
        name: "AdminStageMap",
        params: {
          userId,
          estateId: estateId.value,
          stageId: stage.stageId._id,
        },
      });
      return;
    }

    if (
      props.role === UserRoles.CUSTOMER &&
      stage.stageId.status === StageStatus.NOW_SELLING
    ) {
      router.push({
        path: router.currentRoute.value.path,
        query: {
          stageId: stage.stageId._id,
          embed,
          businessId: props.businessId,
        },
      });
    }
  }

  const getItemPathClasses = (stage: StageMapInterface) => [
    getStageStatusClass(stage.stageId?.status),
    { "selling-animation": stage.stageId?.status === StageStatus.NOW_SELLING },
    { "admin-highlight": props.role === UserRoles.SUPER_ADMIN },
    {
      "not-clickable":
        props.role === UserRoles.CUSTOMER &&
        stage.stageId?.status !== StageStatus.NOW_SELLING,
    },
    {
      "stage-side-panel-hover":
        stageSidePanelHover.value?.value &&
        stageSidePanelHover.value?.id === stage.stageId?._id,
    },
    {
      "stage-side-panel":
        stage.stageId?.status === StageStatus.NOW_SELLING && isEmbeddedDesktop,
    },
    { "stage-plans-view": props.plansView },
  ];

  const getTooltipText = (stageMap: StageMapInterface) => {
    const stage = stageMap.stageId;

    if (stage.status === StageStatus.NOW_SELLING) {
      return `<div><p>STAGE ${stage.title}</p>
      <strong>${stage.status.toUpperCase()}</strong>
      <br><p class="available-lots">${
        props.builderView
          ? props.lotsByHouseDesigns?.length
          : stage.availableLotsCount
      } Available lots</p>
      <p>Click to view</p></div>`;
    }

    return `<div><p>STAGE ${stage.title}</p>
    <strong>${stage.status.toUpperCase()}</strong>
    <br><p class="available-lots">${
      props.builderView
        ? props.lotsByHouseDesigns?.length
        : stage.availableLotsCount
    } Available lots</p></div>`;
  };

  onMounted(() => {
    const mc = new Hammer(svgContainerMap.value);

    mc.get("pinch").set({ enable: true });
    mc.on("pinch", (event: HammerInput) => {
      pinchZoomHammer(event.scale, (delta) => emit("updateZoom", delta));
    });

    mc.on("panstart", (event: HammerInput) => {
      svgStartPosition.value = { x: event.center.x, y: event.center.y };
      svgStartScrollPosition.value = {
        x: svgContainerMap.value.scrollLeft,
        y: svgContainerMap.value.scrollTop,
      };
    });

    mc.on("pan", (event: HammerInput) => {
      const deltaX = event.center.x - svgStartPosition.value.x;
      const deltaY = event.center.y - svgStartPosition.value.y;

      svgContainerMap.value.scrollLeft =
        svgStartScrollPosition.value.x - deltaX;
      svgContainerMap.value.scrollTop = svgStartScrollPosition.value.y - deltaY;
    });
  });

const __returned__ = { emit, store, mobileLayoutWidth, isDesktop, userId, estateId, map, stages, props, stageSidePanelHover, showConfirmDeleteModal, getAvailableStages, isEmbeddedDesktop, getEstateId, onSidePanelStageHover, onSidePanelStageClick, isCustomerRole, hoverStageId, onStageHover, onStageLeave, isDragging, svgStartPosition, svgStartScrollPosition, svgContainerMap, zoomIn, zoomOut, startDrag, handleDrag, stopDrag, getMap, getStages, toCreateMap, toEditMap, deleteMap, onStage, getItemPathClasses, getTooltipText, ConfirmDeleteModal, get router() { return router }, computed, onMounted, ref, watch, get useStore() { return useStore }, get EstateMapInterface() { return EstateMapInterface }, get UserRoles() { return UserRoles }, get StageMapInterface() { return StageMapInterface }, get getStageStatusClass() { return getStageStatusClass }, get StageStatus() { return StageStatus }, get StageInterface() { return StageInterface }, get useIsDesktop() { return useIsDesktop }, StageNavMobile, MapSidePanel, get Hammer() { return Hammer }, get pinchZoomHammer() { return pinchZoomHammer }, get handleDragging() { return handleDragging }, get startDragging() { return startDragging }, get stopDragging() { return stopDragging }, MapSideLegend, EstateSvgMap }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})